import { ReactNode, useState } from 'react';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import Utils from '../../shared/utils';
import { TotalTable } from '../selectionlists/TotalDropDown';
import { MATERIAL_ICON_CART, MATERIAL_ICON_DOWNLOAD } from '../../shared/common/Button/Icon';
import { CheckoutStandingOrderDropDown } from './CheckoutStandingOrderDropDown';
import { CheckoutAccountDropDown } from './CheckoutAccountDropDown';
import { AccountOut, InvoiceOut, OrderOut, OrderRequestIn, StandingOrderOut } from '../../store/api';
import { AuthenticationState } from '../../store/authentication/state';
import { Button, ButtonSize } from '../../shared/common/Button';
import { isFeatureEnabled, VALUE_PLAN_DROPDOWN } from '../../shared/utils/togglz';
import { isAdmin } from '../../store/authentication/actions';
import { mapOrderStatus } from '../orders/orderStatusMapper';
import { useTranslation } from 'react-i18next';
import { DropDownMenu } from '../../shared/common/DropDownMenu';

export type FormDataConfirm = Pick<
    OrderRequestIn,
    'account' | 'specialInstructions' | 'purchaseOrderReference' | 'standingOrder'
>;

type Props = Partial<OrderOut> & {
    readonly authentication: AuthenticationState;
    readonly reserveId?: string;
    readonly accounts: {
        readonly list?: ReadonlyArray<AccountOut>;
        readonly selected?: AccountOut;
        readonly loading?: boolean;
    };
    readonly standingOrders?: {
        readonly list: Array<StandingOrderOut>;
        readonly selected?: StandingOrderOut;
        readonly show: boolean;
    };
    readonly readOnlyFields?: 'all' | ReadonlyArray<string>;
    readonly isOrderDetail?: boolean;
    readonly purchaseOrderReference?: string;
    readonly specialInstructions?: string;
    readonly orderDate?: number;
    readonly status?: string;
    readonly isPlaceOrderDisabled?: boolean;
    readonly isPlaceOrderLoading?: boolean;
    readonly onConfirmation?: (formData: FormDataConfirm) => void;
    readonly onAccountChange?: (account: AccountOut) => void;
    readonly onStandingOrderChange?: (standingOrder: StandingOrderOut) => void;
    readonly onDownloadInvoice?: (invoice: InvoiceOut) => void;
};

export const CheckoutSummary = (props: Props) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [specialInstructions, setSpecialInstructions] = useState<string | null>(null);
    const [purchaseOrderReference, setPurchaseOrderReference] = useState<string | null>(null);
    const [, setShowDropdown] = useState(false);

    function onConfirmation() {
        const specialInstructionsRef = isEmpty(specialInstructions) ? null : specialInstructions;
        const purchaseOrderRef = isEmpty(purchaseOrderReference) ? null : purchaseOrderReference;

        const formData: FormDataConfirm = {
            purchaseOrderReference: purchaseOrderRef ?? undefined,
            specialInstructions: specialInstructionsRef ?? undefined,
            // it is ok to force this type here
            // because the place order button that triggers onConfirmation
            // should be disabled when the account fetching fails.
            account: props.accounts.selected!,
            standingOrder: props.standingOrders?.show ? props.standingOrders.selected : undefined
        };

        props.onConfirmation?.(formData);
    }

    function isReadOnly(fieldName) {
        if (props.readOnlyFields === undefined) {
            return false;
        }
        if (props.readOnlyFields === 'all') {
            return true;
        }
        return includes(props.readOnlyFields, fieldName);
    }

    function buildComponentsByOrderDetailState(): {
        readonly orderDetailPart?: ReactNode;
        readonly specialInstructionsInner?: ReactNode;
        readonly purchaseOrderNoInner?: ReactNode;
        readonly accountInner?: ReactNode;
        readonly standingOrderTitle?: ReactNode;
        readonly standingOrderInner?: ReactNode;
    } {
        if (props.isOrderDetail) {
            return buildComponentsForOrderDetailView();
        } else {
            return buildComponentsForCheckoutView();
        }
    }

    function buildComponentsForCheckoutView() {
        const purchaseOrderNoInner = (
            <input
                type="text"
                readOnly={isReadOnly('purchaseOrderReference')}
                defaultValue={props.purchaseOrderReference}
                placeholder={t('shop.orders.orderNumberPlaceholder')}
                onChange={onPurchaseOrderReferenceChange}
            />
        );
        const specialInstructionsInner = (
            <textarea
                readOnly={isReadOnly('specialInstructions')}
                defaultValue={props.specialInstructions}
                placeholder={t('shop.orders.specialInstructionsPlaceholder')}
                onChange={onSpecialInstructionsChange}
                style={{ height: '20px', overflow: 'hidden' }}
            />
        );
        const accountInner = (
            <CheckoutAccountDropDown accounts={props.accounts} onAccountChange={props.onAccountChange!} />
        );

        let standingOrderInner: ReactNode = null;
        let standingOrderTitle: ReactNode = null;

        if (props.standingOrders) {
            standingOrderInner = (
                <CheckoutStandingOrderDropDown
                    standingOrders={props.standingOrders}
                    onStandingOrderChange={props.onStandingOrderChange!}
                />
            );
            standingOrderTitle = props.standingOrders.show ? (
                <div className="summary-item-title">
                    <strong>{t('shop.orders.standingOrder')}</strong>
                </div>
            ) : null;
        }

        return {
            orderDetailPart: null,
            specialInstructionsInner,
            purchaseOrderNoInner,
            accountInner,
            standingOrderInner,
            standingOrderTitle
        };
    }

    function buildComponentsForOrderDetailView() {
        let accountInner: ReactNode = null;
        let standingOrderInner: ReactNode = null;
        let standingOrderTitle: ReactNode = null;
        const orderDetailPart = (
            <tr className="summary-item">
                <td className="tc tcw-25">
                    <div className="summary-item-title">
                        <strong>{t('shop.orders.orderDate')}</strong>
                    </div>
                    <div>
                        {t('dateFormat.date', {
                            val: new Date(props.orderDate ?? 0)
                        })}
                    </div>
                </td>
                <td className="tc tcw-25">
                    <div className="summary-item-title">
                        <strong>{t('shop.orders.status')}</strong>
                    </div>
                    <div>{t(`shop.orders.${mapOrderStatus(props.status)}` ?? props.status)}</div>
                </td>
            </tr>
        );
        const purchaseOrderNoInner = props.purchaseOrderReference || t('shop.orders.none');
        const specialInstructionsInner = props.specialInstructions || t('shop.orders.none');

        const account = props.account;
        if (account) {
            accountInner = (
                <div>
                    <div>{`${account.type} - ${account.name}`}</div>
                    <div>{account.code}</div>
                </div>
            );
        }
        const standingOrder = props.standingOrder;
        if (standingOrder) {
            standingOrderInner = (
                <div>
                    <div>{standingOrder.planName}</div>
                    <div>{standingOrder.planId}</div>
                </div>
            );
            standingOrderTitle = (
                <div className="summary-item-title">
                    <strong>{t('shop.orders.standingOrder')}</strong>
                </div>
            );
        }
        return {
            orderDetailPart,
            specialInstructionsInner,
            purchaseOrderNoInner,
            accountInner,
            standingOrderInner,
            standingOrderTitle
        };
    }

    function onPurchaseOrderReferenceChange(event) {
        setPurchaseOrderReference(event.target.value);
    }

    function onSpecialInstructionsChange(event) {
        const element = event.target;
        setSpecialInstructions(element.value);
        element.style.height = 0;
        element.style.height = `${element.scrollHeight}px`;
    }

    function onOpen() {
        setShowDropdown(true);
    }

    function onClose() {
        setShowDropdown(false);
    }

    let confirmationButton: ReactNode = null;
    if (props.onConfirmation && Utils.isUserAllowedToOrder(props.authentication)) {
        confirmationButton = (
            <Button
                text={t('shop.orders.placeOrder')}
                onClick={onConfirmation}
                icon={MATERIAL_ICON_CART}
                size={ButtonSize.LARGE}
                fullWidth={true}
                disabled={props.isPlaceOrderLoading || props.isPlaceOrderDisabled}
                isLoading={props.isPlaceOrderLoading}
            />
        );
    }
    let invoiceDownloadLink: ReactNode = null;

    if (props.invoices && !props.onConfirmation) {
        if (props.invoices.length > 1) {
            invoiceDownloadLink = (
                <DropDownMenu
                    expanded={expanded}
                    onToggleClick={() => setExpanded(!expanded)}
                    align="left"
                    gap={3}
                    onClickOutside={() => setExpanded(false)}
                    label={`\u00A0${t('shop.orders.downloadInvoices')}`}
                    labelClassName="txt-normal"
                    className="checkout-invoice-dd"
                    iconCode="file_download"
                    iconDefaultClasses="material-icons small"
                    showActiveArrow={false}
                    onOpenListener={onOpen}
                    onCloseListener={onClose}
                >
                    {props.invoices.map((invoice, i) => {
                        return (
                            <li key={i}>
                                <a onClick={() => props.onDownloadInvoice?.(invoice)}>
                                    <i className="material-icons">file_download</i>&nbsp;Invoice&nbsp;
                                    {invoice.invoiceNumber} <span>({invoice.date})</span>
                                </a>
                            </li>
                        );
                    })}
                </DropDownMenu>
            );
        } else if (props.invoices.length === 1) {
            const invoice = props.invoices[0];
            invoiceDownloadLink = (
                <Button
                    text={t('shop.orders.download', {
                        invoiceType: invoice.invoiceType,
                        invoiceNumber: invoice.invoiceNumber
                    })}
                    icon={MATERIAL_ICON_DOWNLOAD}
                    onClick={() => props.onDownloadInvoice?.(invoice)}
                    size={ButtonSize.LARGE}
                    fullWidth={true}
                />
            );
        }
    }

    const components = buildComponentsByOrderDetailState();
    const orderDetailPart = components.orderDetailPart;
    const specialInstructionsInner = components.specialInstructionsInner;
    const purchaseOrderNoInner = components.purchaseOrderNoInner;
    const accountInner = components.accountInner;
    let standingOrderTitle: ReactNode = null;
    let standingOrderInner: ReactNode = null;
    if (isFeatureEnabled(VALUE_PLAN_DROPDOWN) && isAdmin(props.authentication)) {
        standingOrderTitle = components.standingOrderTitle;
        standingOrderInner = components.standingOrderInner;
    }
    const { billToName, billToStreet, billToBlock, billToCity, billToState, billToZip } = props;
    const address = [billToName, billToStreet, billToBlock, billToCity, `${billToState} ${billToZip}`];
    return (
        <table data-displayname="CheckoutSummary" className="checkout-summary txt-small">
            <tbody>
                <tr className="summary-item">
                    <td className="tc tcw-25">
                        <div className="summary-item-title">
                            <strong>{t('shop.orders.account')}</strong>
                        </div>
                        {accountInner}
                        {standingOrderTitle}
                        {standingOrderInner}
                    </td>
                    <td className="tc tcw-25">
                        <div className="summary-item-title">
                            <strong>{t('shop.orders.billTo')}</strong>
                        </div>
                        <div>
                            {address
                                .filter(part => part && !part.includes('null'))
                                .map((part, i) => {
                                    return <div key={i}>{part}</div>;
                                })}
                        </div>
                    </td>
                    <td className="tc tcw-25">
                        <div className="summary-item-title">
                            <strong>{t && t('shop.orders.addToCollection')}</strong>
                        </div>
                        <div>{props.spocName}</div>
                        <div className="txt-smaller">
                            {t &&
                                t('shop.orders.collection', {
                                    context: props.spocType?.toLowerCase() === 'site' ? 'individual' : 'shared',
                                    defaultValue: ''
                                })}
                        </div>
                    </td>
                    <td className="tc tcw-25">
                        <TotalTable
                            spoc={props.authentication.spoc}
                            currency={props.currency}
                            ebooksAmount={props.totalEBookCopies}
                            eaudiobooksAmount={props.totalEAudiobookCopies}
                            epressAmount={props.totalEPressCopies}
                            totalNetValue={props.netValue}
                            totalTaxValue={props.totalTax}
                            totalValue={props.totalValue}
                            textTotalLine={t('shop.orders.orderTotal')}
                        />
                    </td>
                </tr>

                <tr className="summary-item">
                    <td className="tc tcw-25">
                        <div className="summary-item-title">
                            <strong>{t('shop.orders.purchaseOrderNumber')}</strong>
                        </div>
                        <div>{purchaseOrderNoInner}</div>
                    </td>
                    <td className="tc tcw-50" colSpan={2}>
                        <div className="summary-item-title">
                            <strong>{t('shop.orders.specialInstructions')}</strong>
                        </div>
                        <div>{specialInstructionsInner}</div>
                    </td>
                    <td className="tc tcw-25 confirmation-invoice-btn">
                        {confirmationButton}
                        {invoiceDownloadLink}
                    </td>
                </tr>
                {orderDetailPart}
            </tbody>
        </table>
    );
};

CheckoutSummary.defaultProps = {
    id: null,
    orderDate: null,
    purchaseOrderReference: null,
    deliveredTo: null,
    totalEBooks: 0,
    totalEAudiobooks: 0,
    totalBasePrice: 0,
    discounts: 0,
    totalValue: 0,
    totalTax: 0,
    netValue: 0,
    status: '',
    invoiceId: '',
    invoices: [],
    currency: null,
    billingAddress: '',
    onConfirmation: null,
    readOnlyFields: null,
    isOrderDetail: false,
    authentication: null
};
